import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import {
    CheckCircle,
    Facebook,
    Google,
    HelpOutline,
    Instagram,
    Language,
    Link as LinkIcon,
    YouTube,
} from "@mui/icons-material";
import { GuestOrderRequest, RequestSource } from "../../services/guestOrderService";

const SOURCE_ICON: Record<string, React.ReactElement> = {
    facebook: <Facebook fontSize="small" />,
    instagram: <Instagram fontSize="small" />,
    google: <Google fontSize="small" />,
    youtube: <YouTube fontSize="small" />,
    direct: <LinkIcon fontSize="small" />,
    // Placed before sources were recorded, or the browser blocked storage.
    unknown: <HelpOutline fontSize="small" />,
};

const sourceIcon = (key: string) => SOURCE_ICON[key] ?? <Language fontSize="small" />;

/** "Facebook ad" chip; paid sources are filled so ads stand out in a list. */
export const SourceChip: React.FC<{ source: RequestSource; size?: "small" | "medium" }> = ({ source, size = "small" }) => (
    <Chip
        icon={sourceIcon(source.key)}
        label={source.label}
        size={size}
        color={source.paid ? "primary" : "default"}
        variant={source.paid ? "filled" : "outlined"}
        sx={{ maxWidth: "100%" }}
    />
);

/** The table cell: chip, with the campaign underneath when there is one. */
export const SourceCell: React.FC<{ source?: RequestSource }> = ({ source }) => {
    if (!source) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }
    return (
        <Box sx={{ minWidth: 0, py: 0.5 }}>
            <SourceChip source={source} />
            {source.campaign && (
                <Typography variant="caption" color="text.secondary" noWrap display="block" title={source.campaign}>
                    {source.campaign}
                </Typography>
            )}
        </Box>
    );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) =>
    value ? (
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            <strong>{label}:</strong> {value}
        </Typography>
    ) : null;

/** "Where they came from" in the request details. */
export const RequestSourceDetails: React.FC<{ request: GuestOrderRequest }> = ({ request }) => {
    const source = request.source;
    if (!source) return null;
    const sentAt = request.meta_purchase_sent_at ? new Date(request.meta_purchase_sent_at).toLocaleString() : null;
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box>
                <SourceChip source={source} />
            </Box>
            <Row label="Campaign" value={request.utm_campaign} />
            <Row label="Ad" value={request.utm_content} />
            <Row label="Keyword" value={request.utm_term} />
            {request.utm_source && (
                <Row label="Ad tags" value={[request.utm_source, request.utm_medium].filter(Boolean).join(" / ")} />
            )}
            <Row label="Came from site" value={request.referrer_domain} />
            {/* Just the page — its ad tags are already listed above. */}
            <Row label="First page" value={request.landing_path?.split("?")[0]} />
            {request.has_fbclid && (
                <Typography variant="caption" color="text.secondary">
                    Arrived by clicking a Facebook link
                </Typography>
            )}
            {sentAt ? (
                <Typography variant="caption" color="success.main" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 14 }} /> Sale reported to Meta (Facebook) · {sentAt}
                </Typography>
            ) : request.status === "PENDING_PAYMENT" ? (
                <Typography variant="caption" color="text.secondary">
                    Reported to Meta once the bKash payment is confirmed.
                </Typography>
            ) : null}
        </Box>
    );
};
